<script lang="ts" setup>
import { ref, inject, watch, provide, computed, onMounted } from 'vue';
import CollapseFieldset from '@/components/CollapseFieldset.vue';
import ToggleBox from '@/components/ToggleBox.vue';
import SkyboxFileSelect from '@/components/Lighting/SkyboxFileSelect.vue';
import SkyboxPath from '@/components/Lighting/SkyboxPath.vue';
import SkyboxSize from '@/components/Lighting/SkyboxSize.vue';
import SkyboxRotation from '@/components/Lighting/SkyboxRotation.vue';

import type { Ref } from 'vue';
import type { IManifest, IManifestXYZPosition } from '@/types/IManifest';

const manifest = inject('manifest') as Ref<IManifest>;
const enableSkybox = ref<boolean>(!!manifest.value.skybox);
const useCube = ref<boolean>(manifest.value.skybox?.useCube ?? false);
const skyboxIsInfinite = ref<boolean>(manifest.value.skybox?.skyboxIsInfinite ?? false);
const skyBoxFile = ref('');
const skyBoxPath = ref('');
const skyBoxSize = ref(50);
const skyBoxRotation = ref<IManifestXYZPosition>({ x: 0, y: 0, z: 0});
const availableSkyBoxes = [...__SKYBOXES__];

let settingManifest = false;

const skyBox = computed(() => {
  if (skyBoxPath.value.endsWith('/')) {
    return skyBoxPath.value + skyBoxFile.value;
  }

  return skyBoxPath.value + '/' + skyBoxFile.value;
});

provide('skyBoxPath', skyBoxPath);
provide('skyBoxFile', skyBoxFile);
provide('skyBoxSize', skyBoxSize);
provide('skyBoxRotation', skyBoxRotation);
provide('skyBox', skyBox);

const mountedHandler = () => {
  if (!manifest.value.skybox) return;

  skyBoxSize.value = manifest.value.skybox.skyboxSize;
  skyBoxRotation.value = manifest.value.skybox.skyboxRotation;

  if (!manifest.value.skybox.cubeBaseFileName) return;

  const skyBoxInfo = manifest.value.skybox.cubeBaseFileName.split('/');

  skyBoxFile.value = skyBoxInfo.slice(skyBoxInfo.length - 2, skyBoxInfo.length).join('/');
  skyBoxPath.value = skyBoxInfo.slice(0, skyBoxInfo.length - 2).join('/');
};

const enableSkyboxWatcher = (newValue: boolean) => {
  if (!newValue) {
    manifest.value.skybox = undefined;
    useCube.value = false;
    skyBoxFile.value = '';
    skyBoxPath.value = '';
    skyboxIsInfinite.value = false;
    skyBoxSize.value = 50;
    skyBoxRotation.value = { x: 0, y: 0, z: 0};
  } else if (!settingManifest) {
    manifest.value.skybox = {
      enabled: true,
      useCube: useCube.value,
      skyboxIsInfinite: skyboxIsInfinite.value,
      skyboxSize: 50,
      skyboxRotation: {x: 0, y: 0, z: 0},
    };
  } else {
    settingManifest = false;
  }
};

const useCubeWatcher = (newValue: boolean) => {
  if (!enableSkybox.value || !manifest.value.skybox) return;

  manifest.value.skybox.useCube = newValue;

  if (settingManifest) return;
  if (newValue) {
    const [firstSkybox] = availableSkyBoxes;

    skyBoxFile.value = firstSkybox;
    skyBoxPath.value = '/content/dam/LifeTech/virtual/common/envmap';
  } else {
    skyBoxFile.value = '';
    skyBoxPath.value = '';
  }
};

const skyboxIsInfiniteWatcher = (newValue: boolean) => {
  if (!enableSkybox.value || !manifest.value.skybox) return;

  manifest.value.skybox.skyboxIsInfinite = newValue;
};

const manifestWatcher = (newValue: IManifest, oldValue: IManifest) => {
  if (newValue === oldValue || !manifest.value.skybox) return;

  settingManifest = true;

  enableSkybox.value = true;
  useCube.value = manifest.value.skybox.useCube;
  skyboxIsInfinite.value =  manifest.value.skybox.skyboxIsInfinite;
  skyBoxSize.value =  manifest.value.skybox.skyboxSize;
  skyBoxRotation.value =  manifest.value.skybox.skyboxRotation;

  if (manifest.value.skybox.cubeBaseFileName) {
    const skyBoxInfo =  manifest.value.skybox.cubeBaseFileName.split('/');

    skyBoxFile.value = skyBoxInfo.slice(skyBoxInfo.length - 2, skyBoxInfo.length).join('/');
    skyBoxPath.value = skyBoxInfo.length === 2 ? '/' : skyBoxInfo.slice(0, skyBoxInfo.length - 2).join('/');
  }
};

const skyBoxWatcher = (newValue: string) => {
  if (!manifest.value.skybox) return;

  manifest.value.skybox.cubeBaseFileName = manifest.value.skybox.useCube ? newValue : undefined;
};

onMounted(mountedHandler);
watch(enableSkybox, enableSkyboxWatcher);
watch(useCube, useCubeWatcher);
watch(skyboxIsInfinite, skyboxIsInfiniteWatcher);
watch(manifest, manifestWatcher);
watch(skyBox, skyBoxWatcher);



</script>

<template>
  <CollapseFieldset :enable-collapse="false">
    <template #legend>
      Skybox
    </template>

    <div class="flex flex-col mb-1 last-of-type:mb-0">
      <ToggleBox v-model="enableSkybox" label="Enable Skybox" />
    </div>

    <div v-if="enableSkybox" class="flex flex-col mb-1 last-of-type:mb-0">
      <ToggleBox v-model="skyboxIsInfinite" label="Is Infinite" />
    </div>

    <SkyboxSize v-if="enableSkybox" />

    <div v-if="enableSkybox" class="flex flex-col mb-1">
      <label class="text-xs">Rotation</label>
      <SkyboxRotation />
    </div>

    <div v-if="enableSkybox" class="flex flex-col mb-1 last-of-type:mb-0">
      <ToggleBox v-model="useCube" label="Use Cube" />
    </div>

    <SkyboxFileSelect v-if="useCube" />
    <SkyboxPath v-if="useCube" />
  </CollapseFieldset>
</template>
